import React, { SetStateAction, useContext, useState } from "react";

import { observer } from "mobx-react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { THEME_SETTINGS } from "@theme-settings";

import { EventKey } from "@gp/models";

import { BettingData } from "@data-types/sport-stats/SportStatsTypes";
import { useRootAppStore } from "@hooks";

import BettingTypeStatisticsButton from "@features/offer/desktop/buttons/BettingTypeStatisticsButton";

import { analyticsTracker } from "@utils/analytics";
import { FavoritesStoreContext } from "@state/context/favorites/FavoritesStoreContext";

export const BettingTypeName = observer(function BettingTypeName(props: {
	bettingType: { id: string; simpleName: string };
	offerKey: EventKey;
	name: string;
	bettingTypeStats: BettingData[] | null;
	setBettingTypeStats: React.Dispatch<SetStateAction<BettingData[] | null>>;
	index: number;
	shouldDisplayToolTip: boolean;
}) {
	const [isHover, setIsHover] = useState(false);
	const favoritesStore = useContext(FavoritesStoreContext);
	const isFavorite = favoritesStore.isUserFavoriteBettingType(
		props.bettingType.id,
		props.offerKey.specifier || null
	);
	const rootStore = useRootAppStore();
	const { t } = useTranslation();

	const onButtonClick = () => {
		if (!THEME_SETTINGS.features.isFavoritesEnabled) {
			return;
		}

		if (isFavorite) {
			favoritesStore.removeUserFavoriteBettingType(
				props.bettingType.id,
				props.offerKey.specifier || null
			);
		} else {
			favoritesStore.addUserFavoriteBettingType(
				props.bettingType.id,
				props.offerKey.specifier || null
			);
			analyticsTracker.trackBettingTypeFavorited(
				props.bettingType.simpleName
			);
		}
	};

	const favIconClasses = classNames(
		"offer--additional__bet__fav u-icon u-icon--sml",
		{
			"u-icon--star--str u-color--sent--warning":
				isFavorite ||
				(!isFavorite && isHover && rootStore.userAuthStore.isLoggedIn),
			"u-icon--star--alt u-color--sent--muted":
				(!isFavorite && !isHover) ||
				!rootStore.userAuthStore.isLoggedIn,
			"is-disabled": !rootStore.userAuthStore.isLoggedIn,
		}
	);

	const tooltipMessage = !rootStore.userAuthStore.isLoggedIn
		? t("FAVORITES.NONAUTH_TOOLTIP")
		: "";

	return (
		<div className="offer--additional__bet">
			<div className="offer--additional__bet__type">
				<div
					className="offer--additional__bet__name"
					data-tooltip-id={
						"offerRowTooltip-" + props.name.replace(" ", "")
					}
					data-tooltip-position-strategy="fixed"
					data-tooltip-content={tooltipMessage}
				>
					{THEME_SETTINGS.features.isFavoritesEnabled && (
						<button
							className="at-addToFavsBtn btn btn--reset btn--ghost"
							onClick={onButtonClick}
							onMouseEnter={() => setIsHover(true)}
							onMouseLeave={() => setIsHover(false)}
							disabled={!rootStore.userAuthStore.isLoggedIn}
						>
							<i className={favIconClasses} />
						</button>
					)}
					{props.name}
				</div>

				{props.shouldDisplayToolTip && (
					<i
						className="offer--additional__bet__icon offer--additional__bet__info u-icon u-icon--sml u-icon--info-circle"
						data-tooltip-id={
							"offerRowTooltip-" + props.name.replace(" ", "")
						}
						data-tooltip-position-strategy="fixed"
					/>
				)}
			</div>

			{props.shouldDisplayToolTip && props.index == 0 ? (
				<BettingTypeStatisticsButton
					bettingType={props.bettingType}
					bettingTypeStats={props.bettingTypeStats}
					setBettingTypeStats={props.setBettingTypeStats}
					offerKey={props.offerKey}
				/>
			) : null}
		</div>
	);
});
